import { FormEvent, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { authApi } from '../../api/authApi';
import { getApiErrorMessage } from '../../api/apiClient';
import Spinner from '../../components/Spinner';
import AuthPageShell from './AuthPageShell';
import { getPasswordValidationError, passwordRequirements } from './passwordValidation';
import { Eye, EyeOff } from 'lucide-react';

export default function SetupAccountPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isValidating, setIsValidating] = useState(Boolean(token));
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [error, setError] = useState(token ? '' : 'This invitation link is invalid or missing.');

  useEffect(() => {
    if (!token) return;

    async function validateInvitation() {
      try {
        const response = await authApi.validateSetup(token ? token : '');
        setEmail(response.email);
      } catch (validationError) {
        setError(getApiErrorMessage(validationError, 'This invitation link is invalid or has expired.'));
      } finally {
        setIsValidating(false);
      }
    }

    validateInvitation();
  }, [token]);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    const validationError = getPasswordValidationError(password, confirmPassword);
    if (validationError) return setError(validationError);
    if (!token) return;

    setIsSubmitting(true);
    setError('');
    try {
      await authApi.completeSetup({ token, newPassword: password });
      setIsComplete(true);
    } catch (requestError) {
      setError(getApiErrorMessage(requestError, 'Unable to complete account setup.'));
    } finally {
      setIsSubmitting(false);
    }
  }

  if (isComplete) return <SuccessScreen title="Account Setup Complete" message="Your account has been successfully configured. You can now sign in to Strivre." />;
  if ((!token || error) && !email) return <InvalidLinkScreen message={error} loginLabel="Back to Login" loginPath="/login" />;

  return (
    <AuthPageShell title="Welcome to Strivre">
        {isValidating ? (
        <LoadingState />
        ) : (
        <form
            onSubmit={handleSubmit}
            className="space-y-6"
        >
            <div className="rounded-xl border border-strivre-lightnavy bg-slate-50 p-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-strivre-burgundy">
                Account Setup
            </p>

            <p className="mt-2 text-sm text-slate-500">
                Create your password to activate your account and begin using
                Strivre.
            </p>
            </div>

            <div className="rounded-xl border border-strivre-lightnavy bg-slate-50 p-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Email Address
            </p>

            <p className="mt-1 font-medium text-strivre-navy">
                {email}
            </p>
            </div>

            <PasswordFields
              password={password}
              confirmPassword={confirmPassword}
              onPasswordChange={setPassword}
              onConfirmPasswordChange={setConfirmPassword}
            />

            {error && (
            <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-600">
                {error}
            </div>
            )}

            <button
            type="submit"
            disabled={isSubmitting}
            className="btn-primary flex w-full items-center justify-center gap-2 py-3"
            >
            {isSubmitting && (
                <Spinner size="h-5 w-5" />
            )}

            {isSubmitting
                ? 'Completing Setup...'
                : 'Complete Account Setup'}
            </button>
        </form>
        )}
    </AuthPageShell>
    );
}

export function PasswordFields({
  password,
  confirmPassword,
  onPasswordChange,
  onConfirmPasswordChange,
}: {
  password: string;
  confirmPassword: string;
  onPasswordChange: (value: string) => void;
  onConfirmPasswordChange: (value: string) => void;
}) {
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  return (
    <>
      <div className="space-y-4">
        <label className="block">
          <span className="mb-2 block text-sm font-semibold text-strivre-navy">
            Create Password
          </span>

          <div className="relative">
            <input
              required
              type={showPassword ? "text" : "password"}
              className="input-base w-full pr-12"
              value={password}
              onChange={(event) =>
                onPasswordChange(event.target.value)
              }
            />

            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute inset-y-0 right-3 flex items-center text-slate-500 hover:text-strivre-burgundy"
            >
              {showPassword ? (
                <EyeOff size={18} />
              ) : (
                <Eye size={18} />
              )}
            </button>
          </div>
        </label>

        <label className="block">
          <span className="mb-2 block text-sm font-semibold text-strivre-navy">
            Confirm Password
          </span>

          <div className="relative">
            <input
              required
              type={showConfirmPassword ? "text" : "password"}
              className="input-base w-full pr-12"
              value={confirmPassword}
              onChange={(event) =>
                onConfirmPasswordChange(event.target.value)
              }
            />

            <button
              type="button"
              onClick={() =>
                setShowConfirmPassword(!showConfirmPassword)
              }
              className="absolute inset-y-0 right-3 flex items-center text-slate-500 hover:text-strivre-burgundy"
            >
              {showConfirmPassword ? (
                <EyeOff size={18} />
              ) : (
                <Eye size={18} />
              )}
            </button>
          </div>
        </label>
      </div>

      <div className="rounded-xl border border-strivre-lightnavy bg-slate-50 p-4">
        <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-strivre-burgundy">
          Password Requirements
        </p>

        <ul className="space-y-2 text-sm text-slate-600">
          {passwordRequirements.map(
            (requirement) => (
              <li key={requirement}>
                • {requirement}
              </li>
            )
          )}
        </ul>
      </div>
    </>
  );
}

export function LoadingState() {
  return (
    <div className="flex flex-col items-center py-12">
      <Spinner size="h-8 w-8" />

      <p className="mt-4 text-sm text-slate-500">
        Validating your invitation...
      </p>
    </div>
  );
}

export function InvalidLinkScreen({
  message,
  loginLabel,
  loginPath,
}: {
  message: string;
  loginLabel: string;
  loginPath: string;
}) {
  return (
    <AuthPageShell title="Link Unavailable">
      <div className="space-y-6 text-center">
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-red-50 text-2xl font-bold text-red-600">
          !
        </div>

        <div>
          <p className="text-slate-500">
            {message}
          </p>
        </div>

        <Link
          className="btn-secondary block w-full py-3 text-center"
          to={loginPath}
        >
          {loginLabel}
        </Link>
      </div>
    </AuthPageShell>
  );
}

export function SuccessScreen({
  title,
  message,
}: {
  title: string;
  message: string;
}) {
  return (
    <AuthPageShell title={title}>
      <div className="space-y-6 text-center">
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-emerald-50 text-2xl font-bold text-emerald-600">
          ✓
        </div>

        <div>
          <p className="text-slate-500">
            {message}
          </p>
        </div>

        <Link
          className="btn-primary block w-full py-3 text-center"
          to="/login"
        >
          Go to Login
        </Link>
      </div>
    </AuthPageShell>
  );
}